import { useContext, useState } from 'react';
import styled from 'styled-components';

import { ReportsContext } from 'contexts/reporting';
import { ACCOUNT, COURSES, TEAM_ACTIVITY, USER_ACTIVITY } from 'constants/reporting';
import { SessionContext } from 'contexts/session';
import { screenMd } from 'constants/styles/breakpoints';
import Button from 'components/button';
import { useFlags } from 'launchdarkly-react-client-sdk';

const STICKY_TOP_OFFSET = 'calc(var(--layout-top-bar-height, 0px) + 65px)';

const FilterBar = styled.div`
  position: sticky;
  top: ${STICKY_TOP_OFFSET};
  z-index: 99;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 16px;
  padding: 12px 16px;
  background: var(--white);
  box-shadow: 0px 12px 37px -15px rgba(34, 34, 34, 0.06);
  border-radius: 12px;

  ${screenMd} {
    position: sticky;
    top: ${STICKY_TOP_OFFSET};
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    border-radius: 10px;
  }
`;

const FlapContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const Flap = styled.div<{ active?: boolean; fetching?: boolean }>`
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 14px;
  text-transform: uppercase;
  padding: 6px 12px;
  border-radius: 10px;
  cursor: ${({ fetching }) => (fetching ? 'progress' : 'pointer')};
  color: ${({ theme }) => theme.default.colors.fonts.dark};

  ${({ active, theme }) =>
    active &&
    `
      background-color: ${theme.default.colors.palette.Business_DataVizLilacLighter};
      color: var(--white);
    `}
`;

const RightSummary = styled.div`
  display: none;
  color: var(--light-gray-darker);
  font-size: 12px;

  ${screenMd} {
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const ReportingContentFilter = ({
  onFilterTabChange,
}: {
  onFilterTabChange?: (id: string) => void;
}): JSX.Element => {
  const { fetching, activeFilters } = useContext(ReportsContext);
  const { useAutomatedReports } = useFlags();
  const [activeContent, setActiveContent] = useState(USER_ACTIVITY);

  const { isAccountOwner } = useContext(SessionContext);

  const onClickFlapHandler = (id: string) => {
    if (!fetching) {
      setActiveContent(id);
      onFilterTabChange?.(id);
    }
  };

  return (
    <FilterBar>
      <FlapContainer>
        <Flap
          fetching={fetching}
          active={activeContent === COURSES}
          onClick={() => onClickFlapHandler(COURSES)}
        >
          Course
        </Flap>
        <Flap
          fetching={fetching}
          active={activeContent === TEAM_ACTIVITY}
          onClick={() => onClickFlapHandler(TEAM_ACTIVITY)}
        >
          Team
        </Flap>
        {isAccountOwner && (
          <Flap
            fetching={fetching}
            active={activeContent === ACCOUNT}
            onClick={() => onClickFlapHandler(ACCOUNT)}
          >
            Account
          </Flap>
        )}
        <Flap
          fetching={fetching}
          active={activeContent === USER_ACTIVITY}
          onClick={() => onClickFlapHandler(USER_ACTIVITY)}
        >
          User
        </Flap>
      </FlapContainer>
      <RightSummary>
        <span>
          Selected: {activeFilters?.length || 0}{' '}
          {activeFilters?.length === 1 ? 'metric' : 'metrics'}
        </span>
        {useAutomatedReports && (
          <Button
            $secondary
            size="small"
            disabled={fetching || (activeFilters?.length || 0) === 0}
            onClick={() => window.dispatchEvent(new CustomEvent('reporting:schedule'))}
          >
            Schedule report
          </Button>
        )}
        <Button
          $primary
          size="small"
          disabled={fetching || (activeFilters?.length || 0) === 0}
          onClick={() => window.dispatchEvent(new CustomEvent('reporting:generate'))}
        >
          Generate report
        </Button>
      </RightSummary>
    </FilterBar>
  );
};

export default ReportingContentFilter;
